import { InformationCircleIcon } from '@heroicons/react/24/outline';

import {
  MessageV2DTO,
  PartnerDTO,
  TransactionDTO,
  TransactionOriginApp,
  TransactionState,
  ValueOf,
} from 'support/types';
import { Badge } from 'components/Display/Badge/Badge';
import { Tooltip, TooltipContent, TooltipTrigger } from 'components/Display/Tooltip/Tooltip';

import {
  deriveState,
  extractConnector,
  extractInternalState,
  extractStateContext,
  getColourAndText,
  getReceiverPartnerId,
  wasDeliveredWithoutEmail,
} from './helpers';

type TransactionStatusDisplayProps = {
  state?: ValueOf<TransactionState> | null | undefined;
  transaction?: MessageV2DTO | TransactionDTO | null;
  currentPartner?: PartnerDTO | null;
  originApp?: TransactionOriginApp;
};

export function TransactionStatusDisplay({
  state,
  transaction,
  currentPartner,
  originApp,
}: TransactionStatusDisplayProps) {
  state = state ?? transaction?.state;

  if (!state) {
    return null;
  }

  const derivedState = transaction && currentPartner ? deriveState(state, transaction, currentPartner) : state;

  const isCurrentPartnerReceiver = Boolean(
    transaction && currentPartner && getReceiverPartnerId(transaction) === currentPartner.id,
  );

  const deliveredWithoutEmail = wasDeliveredWithoutEmail(transaction, originApp);

  const { colour, text, tooltip } = getColourAndText(
    derivedState,
    extractStateContext(transaction),
    extractConnector(transaction),
    isCurrentPartnerReceiver,
    extractInternalState(transaction),
    deliveredWithoutEmail,
  );

  const showInfoIcon =
    derivedState === TransactionState.pending || (derivedState === TransactionState.success && deliveredWithoutEmail);

  return (
    <Tooltip>
      <TooltipTrigger>
        <span className="inline-flex items-center gap-0.5">
          <Badge hasDot color={colour}>
            {text}
          </Badge>
          {showInfoIcon && (
            <InformationCircleIcon className="ml-1 size-4 text-gray-400 hover:text-procuros-green-500" />
          )}
        </span>
      </TooltipTrigger>
      <TooltipContent>{tooltip}</TooltipContent>
    </Tooltip>
  );
}
